import { Injectable } from '@angular/core';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { environment } from '../../../../environments/environment';
import { initializeApp } from 'firebase/app';
import { UserService } from '../user.service';
import { NotificationService } from './notification.service';
import { FirebaseEventService } from './firebase-event.service';
import { AuthDataService } from '../../../modules/auth/services/auth.data.service';

@Injectable({
  providedIn: 'root',
})
export class FirebaseMessagingService {
  private messaging = getMessaging(initializeApp(environment.firebase));

  constructor(
    private currentUser: UserService,
    private notificationService: NotificationService,
    private firebaseEventService: FirebaseEventService,
    private authDataService: AuthDataService,
  ) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      console.log('Firebase: listening addEventListener');
      if (event.data?.type === 'NOTIFICATION_CLICKED') {
        this.notificationService.triggerNotificationClick(event.data.payload);
      } else if (event.data?.type === 'UPDATE_NOTIFICATION_COUNTER') {
        this.notificationService.updateNotificationCounter(event.data.notificationCounter);
      } else if (event.data?.type === 'FIREBASE_EVENT') {
        // Background event forwarded from service worker — route through event service
        this.firebaseEventService.dispatch(event.data.payload as Record<string, string>);
      }
    });
  }

  async requestPermissionAndGetToken(): Promise<string | null> {
    try {
      const token = await getToken(this.messaging, {
        vapidKey: environment.firebase.vapidKey,
      });
      if (token) {
        this.currentUser.setFCMToken(token);

        // If user is already logged in, sync the token to the backend.
        // This handles the race condition where FCM token resolves after login.
        if (this.currentUser.isLoggedIn()) {
          this.authDataService.updateFcmToken(token).subscribe({
            error: (err) => console.warn('Failed to sync FCM token to backend:', err),
          });
        }

        return token;
      }
      console.warn('No FCM registration token available.');
      return null;
    } catch (err) {
      console.error('Error retrieving FCM token:', err);
      return null;
    }
  }

  listenForMessages(): void {
    console.log('Firebase: listening for messages');
    onMessage(this.messaging, (payload) => {
      console.log('Firebase message received:', payload);
      const data = payload?.data ?? {};
      const firebaseType = data['firebase_type'];

      if (firebaseType === 'event') {
        console.log('Firebase event received:', data);
        // Delegate all event routing to FirebaseEventService
        this.firebaseEventService.dispatch(data as Record<string, string>);
      } else {
        // Default: notification (existing behaviour — fully backwards compatible)
        const counter = data['notification_counter'] != null
          ? Number(data['notification_counter']) : 0;
        this.notificationService.updateNotificationCounter(counter);

        // Notification payloads already carry the routing metadata built by the
        // backend notification helpers (module_id + data_id — see
        // HandlesBidNotifications::getMetaData()). Publishing them on the same
        // stream lets a widget refresh itself off "something changed in module
        // X" without a dedicated event type per module.
        //
        // Safe for existing subscribers: they all key off a specific eventType
        // ('gallery_folder_ready', 'document_zip_*') or a matching model_id, and
        // a notification payload carries neither.
        this.firebaseEventService.dispatch(data as Record<string, string>);
      }
    });
  }
}
